import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
// Services
import { ButtonEventsService } from '../../services/button-events.service';
import { CmxLayoutService } from '../../services/cmx-layout.service';
// Types
import { Direction } from '../../types/directions';
import { IHeaderMenu, RenderPopoutContent } from '../../types/headerMenu';
import { ILanguage } from '../../types/language';
import { NotificationsService } from '../../services/notifications.service';
import { CwcModal, CwcPopover } from '@cmx-web-components/angular';
import { LegalEntityService } from '../../services/legal-entity.service';
import { Store } from '@ngrx/store';
import { getAuth, getLanguage, State } from '@cemex/cmx-client-layout';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features/models/features.enum';
import { startWith, switchMap } from 'rxjs/operators';

@Component({
  selector: 'cmx-header',
  templateUrl: './cmx-header.component.html',
  styleUrls: ['./cmx-header.component.scss', './cmx-nav-header.component.scss'],
})
export class CmxHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('navMenuRef', { static: true })
  navMenuRef: ElementRef;
  @ViewChild('appMenuRef', { static: true })
  appMenuRef: ElementRef;

  direction: Direction;
  isMobile: boolean;
  isNavMenuOpen = false;
  isLanguageMenuOpen = false;
  activeLanguageId: number;
  openedSubmenu: null | string | number = null;
  isGreen: boolean;
  isAppMenuOpen = false;
  appItemHovered: number;

  private subscriptions: Subscription[] = [];

  version: number;
  needHelpLabel: string;
  ServiceCenterApp: string;
  contactLabel: string;
  feedbackLabel: string;
  notificationsLabel: string;
  notificationsLoadingLabel: string;
  notificationsDelayLabel: string;
  notificationsErrorLabel: string;
  notificationsErrorLinkRefresh: string;
  noNotificationsLabel: string;
  notificationsViewAllLabel: string;
  developerCenterLabel: string;
  settingsLabel: string;
  logoutLabel: string;
  myProfileLabel: string;
  menuButtonLabel: string;
  variant: string;
  brandLogo: string;
  isModalOpen = false;
  isCollapsed = true;
  renderPopoutContent: RenderPopoutContent = {};
  fullName: string;
  showChatbot = false;
  chatbotRealocated = false;
  chatbotLabel = '';

  public activeLanguage$: Observable<string>;

  @ViewChild('countrySupportContactModal', { static: true })
  public countrySupportContactModal: CwcModal;
  @ViewChild('notificationDetailDialog', { static: true })
  public notificationDetailDialog: CwcModal;
  @ViewChild('helpPopover', { static: true })
  public helpPopover: CwcPopover;
  @ViewChild('notificationsPopover', { static: true })
  public notificationsPopover: CwcPopover;
  @ViewChild('consolesPopover', { static: true })
  public consolesPopover: CwcPopover;
  @ViewChild('profilePopover', { static: true })
  public profilePopover: CwcPopover;

  public _showServiceCenter = false;

  public showMyProfile =
    window['CX_SHOW_MYPROFILE'] && (window['CX_SHOW_MYPROFILE'] === 'true' || window['CX_SHOW_MYPROFILE'] === true)
      ? true
      : false;

  constructor(
    public layout: CmxLayoutService,
    public buttons: ButtonEventsService,
    public notificationsService: NotificationsService,
    public legalEntity: LegalEntityService,
    private store: Store<State>,
    private featureToggle: FeatureToggleService,
  ) {
    this.activeLanguage$ = this.store.select(getLanguage);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.layout.isMobile.subscribe((value) => (this.isMobile = value)),
      this.layout.direction.subscribe((value) => (this.direction = value)),
      this.layout.activeLanguageId.subscribe((value) => (this.activeLanguageId = value)),
      this.layout.isGreen.subscribe((value) => (this.isGreen = value)),
      this.layout.version.subscribe((value) => (this.version = value)),
      this.layout.needHelpLabel.subscribe((value) => (this.needHelpLabel = value)),
      this.layout.ServiceCenterApp.subscribe((value) => (this.ServiceCenterApp = value)),
      this.layout.feedbackLabel.subscribe((value) => (this.feedbackLabel = value)),
      this.layout.contactLabel.subscribe((value) => (this.contactLabel = value)),
      this.layout.menuButtonLabel.subscribe((value) => (this.menuButtonLabel = value)),
      this.layout.variantHeader.subscribe((value) => (this.variant = value)),
      this.layout.brandLogo.subscribe((value) => (this.brandLogo = value)),
      this.layout.isCollapsed.subscribe((value) => (this.isCollapsed = value)),
      this.layout.userName.subscribe((value) => {
        const name = value.split(' ');
        // concat all names without spaces
        this.fullName = name.reduce((acc, curr) => (acc + ' ' + curr.trim()).trim(), '');
      }),
      this.layout.chatbotLabel.subscribe((value) => (this.chatbotLabel = value)),
      this.notificationsService.notificationsViewAllLabel.subscribe(
        (value) => (this.notificationsViewAllLabel = value),
      ),
      this.notificationsService.notificationsLabel.subscribe((value) => (this.notificationsLabel = value)),
      this.notificationsService.notificationsDelayLabel.subscribe((value) => (this.notificationsDelayLabel = value)),
      this.notificationsService.notificationsErrorLabel.subscribe((value) => (this.notificationsErrorLabel = value)),
      this.notificationsService.notificationsErrorLinkRefresh.subscribe(
        (value) => (this.notificationsErrorLinkRefresh = value),
      ),
      this.notificationsService.noNotificationsLabel.subscribe((value) => (this.noNotificationsLabel = value)),
      this.notificationsService.notificationsLoadingLabel.subscribe(
        (value) => (this.notificationsLoadingLabel = value),
      ),
      this.legalEntity.isModalOpen.subscribe((value) => (this.isModalOpen = value)),
      this.featureToggle.onConfigurationFetched
        .pipe(
          startWith({}),
          switchMap(() => this.store.select(getAuth)),
        )
        .subscribe(() => {
          this.showChatbot = this.featureToggle.isFeatureEnabled(Features.ChatBotAivo);
          this.chatbotRealocated = this.featureToggle.isFeatureEnabled(Features.ChatBotAivoRealocated);
        }),
    );
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.navMenuRef && !this.navMenuRef.nativeElement.contains(event.target)) {
      if (this.isNavMenuOpen) {
        this.isNavMenuOpen = false;
      }
    }
    if (this.appMenuRef && !this.appMenuRef.nativeElement.contains(event.target)) {
      if (this.isAppMenuOpen) {
        this.isAppMenuOpen = false;
      }
    }
  }

  openMobileMenu() {
    this.layout.isMobileMenuOpen.next(true);
  }

  toggleNavMenu() {
    this.isNavMenuOpen = !this.isNavMenuOpen;
  }

  toggleAppMenu() {
    this.isAppMenuOpen = !this.isAppMenuOpen;
  }

  toggleLanguageMenu() {
    this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
    this.isLanguageMenuOpen && (this.openedSubmenu = null);
  }

  toggleSubmenu(item: IHeaderMenu) {
    if (item.subMenu && item.subMenu.length > 0) {
      this.openedSubmenu === item.id ? (this.openedSubmenu = null) : (this.openedSubmenu = item.id);
    } else {
      this.headerMenuClick(item.id);
    }
    this.isLanguageMenuOpen && (this.isLanguageMenuOpen = false);
  }

  headerMenuClick(id: number | string) {
    this.layout.headerMenuItemClick.emit(id);
    this.openedSubmenu = null;
    this.isNavMenuOpen = false;
  }

  appMenuClick(id: number | string) {
    this.layout.appMenuClick.emit(id);
  }

  changeLanguage(language: ILanguage) {
    this.layout.languageChange.emit(language.languageId);
    this.isLanguageMenuOpen = false;
    this.isNavMenuOpen = false;
    if (this.version === 3) {
      // click event to close all popovers
      document.body.click();
    }
  }

  logout() {
    this.buttons.logout.emit();
    this.isNavMenuOpen = false;
  }

  userClick() {
    this.buttons.user.emit();
    this.isNavMenuOpen = false;
  }

  ngOnDestroy() {
    this.subscriptions.map((sub) => sub.unsubscribe());
  }

  public gotoHome(): void {
    window.location.href = '/';
  }

  public openAivoChat(): void {
    window['$aivo']?.chat.open();
  }

  public goToSupportCenter(): void {
    // navigate to support center (TO DO)
    if (this.version === 3) {
      this.helpPopover.toggle();
    }
  }

  public openCountrySupportContactModal(): void {
    if (this.version === 3) {
      this.helpPopover.toggle();
    }
    if (this.countrySupportContactModal) {
      this.countrySupportContactModal.open = true;
    }
  }

  toggleCollapse() {
    this.layout.isCollapsed.next(!this.isCollapsed);
  }

  renderContent(key: string) {
    this.renderPopoutContent[key] = true;
  }

  headerRendered(isRendered) {
    this.layout.headerRendered.next(true);
  }
}
